import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Link,
  Alert
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { saveSignupProgress, getSignupProgress, SignupStep, shouldRedirectToProgress } from '../utils/signupProgress';
import { BRAND_NAME, BIG_LOGO_PATH } from '../config';

const keyPoints = [
  {
    icon: ArticleOutlinedIcon,
    text: 'We use your information to create an account, show ads and content you might like, and improve our products.',
  },
  {
    icon: ShieldOutlinedIcon,
    text: 'You may choose to provide information about yourself that could have special protections under privacy laws in your area.',
  },
  {
    icon: SettingsOutlinedIcon,
    text: 'You may access, change, or delete your information any time.',
  },
  {
    icon: GroupsOutlinedIcon,
    text: 'People who use our service may have uploaded your contact information.',
  },
];

function TermsAgreement() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || '';
  const formDataFromState = location.state?.formData;
  const emailVerified = location.state?.emailVerified || false;

  // Check for saved progress and restore if needed
  useEffect(() => {
    const redirect = shouldRedirectToProgress('/terms-agreement');
    if (redirect) {
      if (redirect.step === SignupStep.PROFILE) {
        navigate('/user-profile', { state: redirect.data });
        return;
      } else if (redirect.step === SignupStep.SAMPLE_VIDEOS) {
        navigate('/sample-videos', { state: redirect.data });
        return;
      }
    }

    // If no state data but we have saved progress, restore it
    if (!emailFromState && !formDataFromState) {
      const savedProgress = getSignupProgress();
      if (savedProgress && savedProgress.step === SignupStep.TERMS && savedProgress.data) {
        // Data will be available through savedProgress.data
        // We'll use it in the component
      }
    }

    // Save progress when component mounts
    const progressData = {
      email: emailFromState || getSignupProgress()?.data?.email || '',
      formData: formDataFromState || getSignupProgress()?.data?.formData || {},
      emailVerified: emailVerified || getSignupProgress()?.data?.emailVerified || false
    };

    if (progressData.email) {
      saveSignupProgress(SignupStep.TERMS, progressData);
    }
  }, [navigate, emailFromState, formDataFromState, emailVerified]);

  const handleAgree = () => {
    const finalFormData = formDataFromState || getSignupProgress()?.data?.formData || {};
    const finalEmail = emailFromState || getSignupProgress()?.data?.email || '';

    // Save progress to profile step
    saveSignupProgress(SignupStep.PROFILE, {
      email: finalEmail,
      formData: finalFormData
    });

    navigate('/user-profile', {
      state: {
        email: finalEmail,
        formData: finalFormData
      }
    });
  };

  // Get data from state or saved progress
  const displayEmail = emailFromState || getSignupProgress()?.data?.email || '';
  const displayFormData = formDataFromState || getSignupProgress()?.data?.formData || {};
  const showVerificationMessage = emailVerified || getSignupProgress()?.data?.emailVerified || false;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative' }}>
        <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
          <img
            src={BIG_LOGO_PATH}
            alt={BRAND_NAME}
            style={{
              maxWidth: '120px',
              height: 'auto'
            }}
          />
        </Box>

        {showVerificationMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your email has been verified successfully!
          </Alert>
        )}

        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
          To sign up, read and agree to our terms
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#555555', textAlign: 'center', mb: 4 }}>
          Key points you should know
        </Typography>

        <Stack spacing={3} sx={{ mb: 4 }}>
          {keyPoints.map((point, index) => {
            const IconComponent = point.icon;
            return (
              <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    backgroundColor: '#f0f2f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComponent sx={{ color: '#000000' }} />
                </Box>
                <Typography sx={{ color: '#222222' }}>
                  {point.text}{' '}
                  <Link href="#" underline="none" sx={{ color: '#0b57d0', fontWeight: 500 }}>
                    Learn more.
                  </Link>
                </Typography>
              </Box>
            );
          })}
        </Stack>

        <Typography sx={{ color: '#444444', fontSize: '0.95rem', mb: 4 }}>
          By signing up, you agree to {BRAND_NAME}’s{' '}
          <Link href="#" underline="none" sx={{ color: '#0b57d0', fontWeight: 500 }}>
            Terms
          </Link>
          ,{' '}
          <Link href="#" underline="none" sx={{ color: '#0b57d0', fontWeight: 500 }}>
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link href="#" underline="none" sx={{ color: '#0b57d0', fontWeight: 500 }}>
            Cookies Policy
          </Link>.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          onClick={handleAgree}
          sx={{
            backgroundColor: '#0b57d0',
            borderRadius: 999,
            py: 1.5,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: '1.1rem',
            '&:hover': {
              backgroundColor: '#0a4ab3'
            }
          }}
        >
          I agree
        </Button>
      </Container>
    </Box>
  );
}

export default TermsAgreement;

